<script lang="ts">
  import { Shell, Icon, type PortalLink, SignIn } from "@steyncd/steyn-design";
  import { api } from "./lib/api";
  import Admin from "./routes/Admin.svelte";
  import Estate from "./routes/Estate.svelte";
  import Ops from "./routes/Ops.svelte";
  import Manage from "./routes/Manage.svelte";
  import Config from "./routes/Config.svelte";
  import Household from "./routes/Household.svelte";
  import People from "./routes/People.svelte";
  import Access from "./routes/Access.svelte";
  import Signals from "./routes/Signals.svelte";
  import Trail from "./routes/Trail.svelte";
  import StepUpModal from "./lib/StepUpModal.svelte";
  import { session } from "./lib/stores.svelte";
  import { glance } from "./lib/glance.svelte";
  import { attention } from "./lib/attention.svelte";
  import { signIn, signOut, atLeast, completeRedirect, explainAuthError } from "./lib/auth";

  const FRONT_DOOR = import.meta.env.VITE_FRONTDOOR_URL ?? "https://steyn-frontdoor.web.app";

  type Section = "estate" | "ops" | "manage" | "admin" | "access" | "signals" | "trail" | "config" | "household" | "people";
  let section = $state<Section>("estate");

  /**
   * The cross-portal switcher's data source (steyn-design v2, item 20).
   *
   * Fabric passes its own authenticated fetch rather than a URL, because `Shell`
   * has no access to the ID token and no business acquiring one. The registry is
   * already filtered to what the caller may see, so no extra filtering here — and
   * Fabric itself is dropped, since a link to the page you are on is furniture.
   */
  const ICON: Record<string, string> = {
    ha: "house", hq: "finance", screening: "film", codebots: "bot",
    vault: "vault", homestead: "homestead", waypoint: "waypoint",
    hindsight: "hindsight", fabric: "fabric", frontdoor: "door",
  };

  async function loadPortals(): Promise<PortalLink[]> {
    const res = await api.get<{ portals: { id: string; name: string; url: string }[] }>("/portals");
    return res.portals
      .filter((p) => p.id !== "fabric")
      .map((p) => ({ id: p.id, name: p.name, url: p.url, icon: ICON[p.id] ?? "door" }));
  }

  let signingIn = $state(false);
  let signInError = $state<string | null>(null);

  /**
   * Finish a redirect sign-in before deciding what to render. Without this the
   * credential in the URL is never collected and the gate simply reappears —
   * the "I click it and nothing happens" loop.
   */
  let settling = $state(true);
  $effect(() => {
    void completeRedirect()
      .then((r) => {
        // A redirect that came back with a real error used to be swallowed, so the gate
        // just reappeared with nothing saying why. Surface it like any other failure.
        if (r.error) signInError = explainAuthError(r.error);
      })
      .finally(() => (settling = false));
  });

  async function doSignIn() {
    signingIn = true;
    signInError = null;
    try {
      await signIn();
    } catch (e) {
      // Null for a cancelled popup: closing a window is a decision, not a fault.
      signInError = explainAuthError(e);
    } finally {
      signingIn = false;
    }
  }
</script>

{#if !session.ready || settling}
  <!-- Deliberately blank. Flashing the sign-in card and then replacing it with
       the app is worse than 200ms of nothing. -->
  <div class="boot" aria-busy="true"></div>
{:else if !session.user}
  <SignIn
    glance={glance.stats}
    product="Fabric"
    mark="fabric"
    tagline="Who everyone is, what is alive, and what the estate costs."
    onsignin={doSignIn}
    busy={signingIn}
    error={signInError}
    siblings={["Home", "Finance", "Screening Room", "Vault", "Hindsight", "Codebots"]}
  />
{:else if !atLeast(session.claims?.role ?? null, "owner")}
  <div class="gate">
    <div class="card--hero gate__card">
      <h1>Fabric</h1>
      <p class="sub">
        {#if session.claims}
          This screen is owner-only. You are signed in as {session.claims.role}.
        {:else}
          This Google account has no access to the Steyn household.
        {/if}
      </p>
      <a class="btn-primary gate__btn" href={FRONT_DOOR}>Go to the Front Door</a>
      <button class="gate__link" onclick={signOut}>Sign out</button>
    </div>
  </div>
{:else}
  <Shell
    product="Fabric"
    mark="fabric"
    portals={loadPortals}
    nav={[
      { id: "estate", label: "Estate", icon: "fabric" },
      { id: "ops", label: "Ops", icon: "wrench" },
      { id: "manage", label: "Manage", icon: "gear" },
      { id: "admin", label: "People & portals", icon: "users" },
      { id: "people", label: "Who can open what", icon: "shield" },
      { id: "access", label: "Access", icon: "shield" },
      { id: "signals", label: "Digest & flags", icon: "chat" },
      { id: "trail", label: "What changed", icon: "clock" },
      { id: "household", label: "Household", icon: "users" },
      { id: "config", label: "Config", icon: "gear" },
    ]}
    active={section}
    onnavigate={(id) => (section = id as Section)}
    frontDoorUrl={FRONT_DOOR}
  >
    {#snippet header()}
      <!-- Something wants you, wherever you are standing. A number and a link, not a
           notification centre — the Front Door owns the list. -->
      {#if attention.count > 0}
        <a class="wants" href={attention.href} title="{attention.count} thing(s) want you">
          <Icon name="inbox" size={14} />
          <span class="num">{attention.count}</span>
        </a>
      {/if}
      <span class="who">
        <Icon name="user" size={14} />
        <!-- Optional chaining rather than `!`: `session.user` is a rune on a
             class instance, which Svelte cannot narrow across the {:else}
             branches above even though it is provably non-null here. -->
        {session.user?.displayName ?? session.user?.email}
      </span>
      <button class="signout" onclick={signOut} aria-label="Sign out">
        <Icon name="logout" size={16} />
      </button>
    {/snippet}

    {#if section === "estate"}
      <Estate />
    {:else if section === "ops"}
      <Ops />
    {:else if section === "manage"}
      <Manage />
    {:else if section === "access"}
      <Access />
    {:else if section === "signals"}
      <Signals />
    {:else if section === "trail"}
      <Trail />
    {:else if section === "people"}
      <People />
    {:else if section === "household"}
      <Household />
    {:else if section === "config"}
      <Config />
    {:else}
      <Admin />
    {/if}
  </Shell>

  <!-- Rendered once, at the root, so any guarded action anywhere can raise it. -->
  <StepUpModal />
{/if}

<style>
  /* Amber because it wants attention, with a glyph and a number beside it — colour
     is never the only signal. */
  .wants {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 4px 9px;
    border-radius: var(--r-pill);
    font-size: var(--fs-micro);
    font-weight: 800;
    color: var(--warn);
    box-shadow: inset 0 0 0 1px var(--line);
    flex-shrink: 0;
  }
  .wants:hover {
    box-shadow: inset 0 0 0 1px var(--warn);
  }

  .boot {
    min-height: 100vh;
    background: var(--aurora);
  }
  .gate {
    min-height: 100vh;
    background: var(--aurora);
    display: grid;
    place-items: center;
    padding: 24px;
  }
  .gate__card {
    width: min(380px, 100%);
    padding: 30px 28px;
    text-align: center;
  }
  h1 {
    margin: 0 0 6px;
    font-size: 1.375rem;
    font-weight: 800;
    letter-spacing: -0.5px;
    color: var(--tx);
  }
  .gate__btn {
    display: inline-block;
    margin-top: 18px;
    padding: 9px 18px;
    font-size: 0.7812rem;
  }
  .gate__link {
    display: block;
    margin: 12px auto 0;
    font-size: 0.72rem;
    font-weight: 700;
    color: var(--mut);
  }
  .who {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    font-size: 0.72rem;
    font-weight: 700;
    color: var(--mut);
  }
  .signout {
    padding: 6px;
    border-radius: var(--r-control);
    color: var(--mut);
  }
  .signout:hover {
    color: var(--acc);
  }
</style>
